<template>
  <transition
    name="toast"
    appear
    @enter="onEnter"
    @leave="onLeave"
  >
    <div
      v-if="isVisible"
      :class="toastClasses"
      class="toast-notification"
      @click="close"
    >
      <div class="toast-icon">
        <i :class="iconClass"></i>
      </div>
      <div class="toast-content">
        <div class="toast-message">{{ message }}</div>
      </div>
      <button class="toast-close" @click.stop="close">
        <i class="fa-solid fa-times"></i>
      </button>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'ToastNotification',
  props: {
    message: {
      type: String,
      required: true
    },
    type: {
      type: String,
      default: 'info',
      validator: (value) => ['success', 'error', 'warning', 'info'].includes(value)
    },
    toastId: {
      type: [String, Number],
      required: true
    },
    position: {
      type: String,
      default: 'top-right'
    },
    onRemove: {
      type: Function,
      default: () => {}
    }
  },
  data() {
    return {
      isVisible: false,
      isClosing: false
    }
  },
  computed: {
    toastClasses() {
      return [
        'toast-notification',
        `toast-${this.type}`,
        `toast-${this.position}`,
        {
          'toast-closing': this.isClosing
        }
      ];
    },
    iconClass() {
      const icons = {
        success: 'fa-solid fa-check-circle',
        error: 'fa-solid fa-times-circle',
        warning: 'fa-solid fa-exclamation-triangle',
        info: 'fa-solid fa-info-circle'
      };
      return icons[this.type] || icons.info;
    }
  },
  methods: {
    show() {
      this.isVisible = true;
    },
    
    close() {
      if (this.isClosing) return;
      
      this.isClosing = true;
      this.isVisible = false;
      
      // Aguardar transição antes de remover
      setTimeout(() => {
        this.onRemove();
      }, 300);
    },
    
    onEnter(el) {
      // Animação de entrada
      el.style.transform = 'translateX(100%)';
      el.style.opacity = '0';
      
      requestAnimationFrame(() => {
        el.style.transition = 'all 0.3s ease-out';
        el.style.transform = 'translateX(0)';
        el.style.opacity = '1';
      });
    },
    
    onLeave(el) {
      // Animação de saída
      el.style.transition = 'all 0.3s ease-in';
      el.style.transform = 'translateX(100%)';
      el.style.opacity = '0';
    }
  }
}
</script>

<style scoped>
.toast-notification {
  position: relative;
  display: flex;
  align-items: center;
  min-width: 300px;
  max-width: 500px;
  margin-bottom: 0.75rem;
  padding: 1rem 1.25rem;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.3s ease;
  border-left: 4px solid;
  backdrop-filter: blur(10px);
}

.toast-notification:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
}

/* Tipos de toast */
.toast-success {
  border-left-color: var(--success-color, #10b981);
  background: rgba(240, 253, 244, 0.95);
}

.toast-error {
  border-left-color: var(--danger-color, #ef4444);
  background: rgba(254, 242, 242, 0.95);
}

.toast-warning {
  border-left-color: var(--warning-color, #f59e0b);
  background: rgba(255, 251, 235, 0.95);
}

.toast-info {
  border-left-color: var(--info-color, #3b82f6);
  background: rgba(239, 246, 255, 0.95);
}

/* Tema escuro */
[data-theme="dark"] .toast-notification {
  background: rgba(30, 41, 59, 0.95);
  color: var(--text-primary-dark, #f1f5f9);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
}

[data-theme="dark"] .toast-success {
  background: rgba(16, 185, 129, 0.25);
  border-left-color: var(--success-color, #10b981);
}

[data-theme="dark"] .toast-error {
  background: rgba(239, 68, 68, 0.25);
  border-left-color: var(--danger-color, #ef4444);
}

[data-theme="dark"] .toast-warning {
  background: rgba(245, 158, 11, 0.25);
  border-left-color: var(--warning-color, #f59e0b);
}

[data-theme="dark"] .toast-info {
  background: rgba(59, 130, 246, 0.25);
  border-left-color: var(--info-color, #3b82f6);
}

/* Ícone */
.toast-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.75rem;
  font-size: 1.25rem;
}

.toast-success .toast-icon {
  color: var(--success-color, #10b981);
}

.toast-error .toast-icon {
  color: var(--danger-color, #ef4444);
}

.toast-warning .toast-icon {
  color: var(--warning-color, #f59e0b);
}

.toast-info .toast-icon {
  color: var(--info-color, #3b82f6);
}

/* Conteúdo */
.toast-content {
  flex: 1;
  min-width: 0;
}

.toast-message {
  font-size: 0.95rem;
  font-weight: 500;
  line-height: 1.4;
  color: var(--text-primary, #1e293b);
  word-wrap: break-word;
}

[data-theme="dark"] .toast-message {
  color: var(--text-primary-dark, #f1f5f9);
}

/* Botão de fechar */
.toast-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-left: 0.75rem;
  background: none;
  border: none;
  cursor: pointer;
  border-radius: 4px;
  color: var(--text-secondary, #64748b);
  transition: all 0.2s ease;
  font-size: 0.875rem;
}

.toast-close:hover {
  background: rgba(0, 0, 0, 0.1);
  color: var(--text-primary, #1e293b);
}

[data-theme="dark"] .toast-close {
  color: var(--text-secondary-dark, #94a3b8);
}

[data-theme="dark"] .toast-close:hover {
  background: rgba(255, 255, 255, 0.1);
  color: var(--text-primary-dark, #f1f5f9);
}

/* Transições */
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.toast-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

/* Responsividade */
@media (max-width: 640px) {
  .toast-notification {
    min-width: calc(100vw - 2rem);
    max-width: calc(100vw - 2rem);
    margin-left: 1rem;
    margin-right: 1rem;
  }
}
</style>

<style>
/* Container global para os toasts */
.toast-container {
  position: fixed;
  top: 1rem;
  right: 1rem;
  z-index: 9999;
  pointer-events: none;
  max-height: calc(100vh - 2rem);
  overflow: hidden;
}

.toast-container .toast-notification {
  pointer-events: all;
}

/* Responsividade do container */
@media (max-width: 640px) {
  .toast-container {
    top: 1rem;
    right: 0;
    left: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
}
</style> 